import httpHelper from '@/utils/modules/http.helper'

import formatHelper from '@/utils/modules/format.helper.js'
import moment from 'moment'

const state = {
    isSidebarOpen: true,
    isSubchildExpanded: false,
    truckDetails: null,
    isTruckDataFetching: false,
    isRadialSearchLoading: false,
    radialSearchCenter: null,
    radialSearchRadius: 100,
    radialSearchZone: null,
    radialSearchAddress: null,
    radialSearchAssetTypes: [],
    radialSearchMode: 'address',
    radialSearchStart: null,
    radialSearchEnd: null,
    radialSearchResults: null,
    driverTimes: null,
    currentDriverId: null,
    isDriverTimesFetching: false,
}

const getters = {
    canPerformRadialSearch(state) {
        return (
            state.radialSearchCenter &&
            state.radialSearchRadius > 0 &&
            state.radialSearchMode === 'address'
        )
    },
    radialSearchPolygon(state) {
        const { lat, lng } = state.radialSearchCenter || {}
        const r = state.radialSearchRadius
        if (!lat || !lng || !r) {
            return []
        }

        const N = 32
        const R = 6378137 // WSG84 Earth radius
        const { cos, sin, PI } = Math
        const DEG = PI / 180

        return Array.from({ length: N })
            .map((_, i) => (2 * PI * i) / N + 1)
            .map(angle => [
                lng + (r * sin(angle)) / (R * DEG * cos(lat * DEG)),
                lat + (r * cos(angle)) / (R * DEG),
            ])
    },
    driverTimesFormatted(state) {
        const times = state.driverTimes
        if (!times) {
            return {}
        }
        const breaks = times?.valid_breaks || []
        const format = formatHelper.hoursAndMinutesDuration
        return {
            workStart: times?.work_start
                ? moment
                      .utc(times.work_start, 'HH:mm:ss')
                      .local()
                      .format('LT')
                : null,
            drivingTotal: format(times?.total_driving_time),
            workTotal: format(times?.total_working_time),
            nextDrivingBreak: format(times?.next_driving_break),
            nextWorkBreak: format(times?.next_working_break),
            dailyLimitDriving: format(times?.daily_limit_driving),
            dailyLimitWork: format(times?.daily_limit_working),
            breaks: breaks.map(format),
            breakTotal: format(breaks.reduce((sum, b) => sum + b, 0)),
        }
    },
}

const mutations = {
    toggleSidebar(state) {
        state.isSidebarOpen = !state.isSidebarOpen
    },
    toggleSubchildExpanded(state) {
        state.isSubchildExpanded = !state.isSubchildExpanded
    },
    setRadialSearchCenter(state, coordinates) {
        state.radialSearchCenter = coordinates
    },
    setRadialSearchAddress(state, address) {
        state.radialSearchAddress = address
    },
    setRadialSearchRadius(state, radius) {
        state.radialSearchRadius = radius
    },
    setRadialSearchMode(state, mode) {
        if (['address', 'zone'].includes(mode)) {
            state.radialSearchMode = mode
        }
    },
    setRadialSearchStart(state, start) {
        state.radialSearchStart = start
    },
    setRadialSearchEnd(state, end) {
        state.radialSearchEnd = end
    },
    setRadialSearchZone(state, zone) {
        state.radialSearchZone = zone
    },
    setRadialSearchLoading(state, isLoading) {
        state.isRadialSearchLoading = isLoading
    },
    setRadialSearchResults(state, results) {
        state.radialSearchResults = results
    },
    setRadialSearchAssetTypes(state, assetTypes) {
        state.radialSearchAssetTypes = assetTypes
    },
    setCurrentDriverId(state, driverId) {
        state.currentDriverId = driverId
    },
    setDriverTimes(state, times) {
        state.driverTimes = times
    },
    setDriverTimesFetching(state, isFetching) {
        state.isDriverTimesFetching = isFetching
    },
}

const actions = {
    async fetchRadialSearchResults({ commit, getters, state }) {
        commit('setRadialSearchLoading', true)
        let polygon = {
            type: 'Polygon',
        }
        if (state.radialSearchMode === 'address') {
            const coordinates = getters.radialSearchPolygon
            polygon.coordinates = [coordinates.concat([coordinates[0]])]
        } else if (state.radialSearchMode === 'zone') {
            polygon.coordinates = state.radialSearchZone?.geofence?.coordinates
        }
        const assetTypes = state.radialSearchAssetTypes || []
        const body = {
            polygon,
            start: state.radialSearchStart,
            end: state.radialSearchEnd,
            ...(assetTypes.length && {
                asset_types: assetTypes,
            }),
        }
        if (!body.polygon.coordinates?.length || !body.start || !body.end) {
            commit('setRadialSearchLoading', false)
            return
        }
        try {
            const response = await httpHelper.post(
                '/polygon-search/',
                body,
                {},
                {
                    apiVersion: 'v2',
                }
            )
            commit('setRadialSearchResults', response.data.results)
        } catch (error) {
            console.error('Error fetching radial search results:', error)
        } finally {
            commit('setRadialSearchLoading', false)
        }
    },
    async fetchAddressForCoordinates(
        { state, commit },
        { language, lat, lng }
    ) {
        if (!lat || !lng || state.radialSearchMode !== 'address') {
            return null
        }
        try {
            commit('setRadialSearchLoading', true)
            const { data } = await httpHelper.get('/positionstack/reverse/', {
                params: {
                    language,
                    limit: 1,
                    query: `${lat},${lng}`,
                },
            })
            commit('setRadialSearchAddress', {
                ...data.data[0],
                latitude: lat,
                longitude: lng,
            })
        } catch (error) {
            console.error('Error fetching address for coordinates:', error)
            return []
        } finally {
            commit('setRadialSearchLoading', false)
        }
    },
    async fetchDriverTimes({ commit, state }, driverId) {
        if (!driverId) {
            return
        }
        try {
            commit('setDriverTimesFetching', true)
            const times = await httpHelper.get(
                `/tachograph/driver-work-summary/${driverId}`
            )
            if (driverId === state.currentDriverId) {
                commit('setDriverTimes', times.data)
            }
        } catch (error) {
            console.error('Error fetching driver times:', error)
        } finally {
            commit('setDriverTimesFetching', false)
        }
    },
}
export default {
    namespaced: true,
    state,
    actions,
    getters,
    mutations,
}
